import { useState, useEffect, useRef } from 'react'
import { File, Folder, Search, Save, Plus, Trash2, Upload, X, RotateCcw, Sparkles } from 'lucide-react'
import { api } from '../../api/client'
// s61 — streamRequest + RegenerateEvent deixaram de ser usados; o
// endpoint /onboarding/regenerate é fire-and-forget agora.

interface VaultFile {
  id: string
  path: string
  filename: string
  size_bytes: number | null
  updated_at: string
}

interface VaultFileContent {
  path: string
  filename: string
  content: string
}

export function VaultPage() {
  const [files, setFiles] = useState<VaultFile[]>([])
  const [selectedFile, setSelectedFile] = useState<VaultFileContent | null>(null)
  const [editContent, setEditContent] = useState('')
  const [searchQuery, setSearchQuery] = useState('')
  const [searchResults, setSearchResults] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [dirty, setDirty] = useState(false)
  const [showNewFileModal, setShowNewFileModal] = useState(false)
  const [newFilePath, setNewFilePath] = useState('')
  const [newFileContent, setNewFileContent] = useState('')
  const [creating, setCreating] = useState(false)
  const [importing, setImporting] = useState(false)
  const [reindexing, setReindexing] = useState(false)
  const [regenerating, setRegenerating] = useState(false)
  const [regenStatus, setRegenStatus] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => { loadFiles() }, [])

  async function loadFiles() {
    try {
      const data = await api.get<VaultFile[]>('/vault/files')
      setFiles(data)
    } catch { /* ignore */ }
    setLoading(false)
  }

  async function openFile(path: string) {
    try {
      const data = await api.get<VaultFileContent>(`/vault/files/${encodeURIComponent(path)}`)
      setSelectedFile(data)
      setEditContent(data.content)
      setDirty(false)
    } catch { /* ignore */ }
  }

  async function saveFile() {
    if (!selectedFile || !dirty) return
    setSaving(true)
    try {
      await api.put(`/vault/files/${encodeURIComponent(selectedFile.path)}`, { content: editContent })
      setDirty(false)
    } catch { /* ignore */ }
    setSaving(false)
  }

  async function handleRegenerate() {
    // s61 — o endpoint passou a ser fire-and-forget (não streaming).
    // Dispara os 3 backfills (email + calendar + contacts) em Celery;
    // a UI não acompanha o progresso aqui (o user pode ir à página
    // Connectors ver o status per-source). Mensagem curta + auto-hide.
    setRegenerating(true)
    setRegenStatus('A re-analisar Gmail e calendário...')
    try {
      const res = await api.post<{ status: string; sources?: string[] }>(
        '/onboarding/regenerate',
      )
      if (res.status === 'already_running') {
        setRegenStatus('Já estava a correr. Volta dentro de uns minutos.')
      } else if (res.status === 'dispatched') {
        setRegenStatus('A correr em background. Os novos ficheiros aparecem quando terminar.')
      } else {
        setRegenStatus('Não consegui disparar a re-análise. Verifica se o Gmail está ligado.')
      }
      setTimeout(() => setRegenStatus(null), 8000)
    } catch (err) {
      setRegenStatus('Erro ao re-analisar.')
      setTimeout(() => setRegenStatus(null), 4000)
    }
    setRegenerating(false)
  }

  async function handleSearch() {
    if (!searchQuery.trim()) { setSearchResults([]); return }
    try {
      const results = await api.get<any[]>(`/vault/search?q=${encodeURIComponent(searchQuery)}&limit=10`)
      setSearchResults(results)
    } catch { setSearchResults([]) }
  }

  async function deleteFile(path: string, e: React.MouseEvent) {
    e.stopPropagation()
    if (!confirm(`Eliminar "${path}"?`)) return
    try {
      await api.delete(`/vault/files/${encodeURIComponent(path)}`)
      setFiles(prev => prev.filter(f => f.path !== path))
      if (selectedFile?.path === path) { setSelectedFile(null); setEditContent('') }
    } catch { /* ignore */ }
  }

  async function createNewFile() {
    if (!newFilePath.trim()) return
    setCreating(true)
    const path = newFilePath.trim().endsWith('.md') ? newFilePath.trim() : newFilePath.trim() + '.md'
    try {
      await api.post(`/vault/files/${encodeURIComponent(path)}`, { content: newFileContent })
      await loadFiles()
      setShowNewFileModal(false)
      setNewFilePath('')
      setNewFileContent('')
      openFile(path)
    } catch { alert('Erro ao criar ficheiro') }
    setCreating(false)
  }

  async function handleImportFiles(e: React.ChangeEvent<HTMLInputElement>) {
    const selected = e.target.files
    if (!selected || selected.length === 0) return
    const mdFiles = Array.from(selected).filter(f => f.name.endsWith('.md') || f.name.endsWith('.txt'))
    if (mdFiles.length === 0) return
    setImporting(true)
    let imported = 0
    for (const file of mdFiles) {
      try {
        const content = await file.text()
        const rel = (file as any).webkitRelativePath || file.name
        const parts = rel.split('/')
        const path = parts.length > 1 ? parts.slice(1).join('/') : parts[0]
        await api.post(`/vault/files/${encodeURIComponent(path)}`, { content })
        imported++
      } catch { /* skip */ }
    }
    await loadFiles()
    setImporting(false)
    e.target.value = ''
    if (imported > 0) alert(`${imported} ficheiro(s) importado(s).`)
  }

  // Group files by directory
  const fileTree = files.reduce<Record<string, VaultFile[]>>((acc, file) => {
    const dir = file.path.includes('/') ? file.path.split('/').slice(0, -1).join('/') : ''
    if (!acc[dir]) acc[dir] = []
    acc[dir].push(file)
    return acc
  }, {})

  return (
    <div className="flex flex-1 overflow-hidden" style={{ background: 'var(--cream)', fontFamily: 'var(--sans)' }}>

      {/* File browser */}
      <div className="w-60 md:w-64 shrink-0 flex flex-col bg-white border-r" style={{ borderColor: 'var(--border)' }}>

        {/* Search */}
        <div className="p-3 border-b" style={{ borderColor: 'var(--border)' }}>
          <div className="flex items-center gap-2 border rounded-md px-2.5 py-1.5 bg-white" style={{ borderColor: 'var(--border)' }}>
            <Search size={13} className="shrink-0" style={{ color: 'var(--ink-muted)' }} strokeWidth={1.5} />
            <input
              type="text"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleSearch()}
              placeholder="Pesquisar no vault..."
              className="flex-1 text-xs bg-transparent focus:outline-none"
              style={{ color: 'var(--ink-light)', }}
            />
            {searchQuery && (
              <button onClick={() => { setSearchQuery(''); setSearchResults([]) }}>
                <X size={11} style={{ color: 'var(--ink-muted)' }} />
              </button>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="px-3 py-2 flex items-center gap-2 border-b" style={{ borderColor: 'var(--border)' }}>
          <button
            onClick={() => setShowNewFileModal(true)}
            className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs rounded-md border transition-colors flex-1"
            style={{ borderColor: 'var(--border)', color: 'var(--ink-light)' }}
            onMouseEnter={e => (e.currentTarget.style.background = 'var(--cream-dark)')}
            onMouseLeave={e => (e.currentTarget.style.background = '')}
          >
            <Plus size={12} strokeWidth={1.5} />
            Novo
          </button>
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={importing}
            className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs rounded-md border transition-colors flex-1 disabled:opacity-50"
            style={{ borderColor: 'var(--border)', color: 'var(--ink-light)' }}
            onMouseEnter={e => (e.currentTarget.style.background = 'var(--cream-dark)')}
            onMouseLeave={e => (e.currentTarget.style.background = '')}
          >
            <Upload size={12} strokeWidth={1.5} />
            {importing ? '...' : 'Importar'}
          </button>
          <button
            onClick={async () => {
              setReindexing(true)
              try {
                const r = await api.post<{ reindexed: number }>('/vault/reindex', {})
                alert(`${r.reindexed} ficheiros reindexados.`)
              } catch { alert('Erro ao reindexar') }
              setReindexing(false)
            }}
            disabled={reindexing}
            title="Reindexar vault"
            className="p-1.5 rounded-md border transition-colors disabled:opacity-50"
            style={{ borderColor: 'var(--border)' }}
            onMouseEnter={e => (e.currentTarget.style.background = 'var(--cream-dark)')}
            onMouseLeave={e => (e.currentTarget.style.background = '')}
          >
            <RotateCcw size={12} className={reindexing ? 'animate-spin' : ''} style={{ color: 'var(--ink-muted)' }} strokeWidth={1.5} />
          </button>
          <button
            onClick={handleRegenerate}
            disabled={regenerating}
            title="Re-analisar Gmail e gerar perfil"
            className="p-1.5 rounded-md border transition-colors disabled:opacity-50"
            style={{ borderColor: 'var(--border)' }}
            onMouseEnter={e => (e.currentTarget.style.background = 'var(--cream-dark)')}
            onMouseLeave={e => (e.currentTarget.style.background = '')}
          >
            <Sparkles size={12} className={regenerating ? 'animate-pulse' : ''} style={{ color: 'var(--ink-muted)' }} strokeWidth={1.5} />
          </button>
          <input ref={fileInputRef} type="file" accept=".md,.txt" multiple
            // @ts-ignore
            webkitdirectory="" className="hidden" onChange={handleImportFiles} />
        </div>

        {/* Regen status */}
        {regenStatus && (
          <div className="px-3 py-2 border-b text-xs" style={{ borderColor: 'var(--border)', color: 'var(--ink-light)', background: 'var(--cream)' }}>
            {regenStatus}
          </div>
        )}

        {/* File tree / search results */}
        <div className="flex-1 overflow-y-auto py-2">
          {loading ? (
            <p className="text-xs px-4 py-3" style={{ color: 'var(--ink-muted)' }}>A carregar...</p>
          ) : searchResults.length > 0 ? (
            <div>
              <div className="flex items-center justify-between px-4 py-1 mb-1">
                <span className="text-xs" style={{ color: 'var(--ink-muted)' }}>Resultados</span>
                <button onClick={() => setSearchResults([])} className="text-xs" style={{ color: 'var(--ink-muted)' }}>Limpar</button>
              </div>
              {searchResults.map((result, i) => (
                <button
                  key={i}
                  onClick={() => { openFile(result.file_path); setSearchResults([]) }}
                  className="w-full text-left px-4 py-2 transition-colors"
                  onMouseEnter={e => (e.currentTarget.style.background = 'var(--cream-dark)')}
                  onMouseLeave={e => (e.currentTarget.style.background = '')}
                >
                  <div className="text-xs truncate font-medium" style={{ color: 'var(--ink-light)' }}>{result.file_path}</div>
                  <div className="text-xs mt-0.5 line-clamp-2" style={{ color: 'var(--ink-muted)' }}>{result.chunk_text?.slice(0, 80)}</div>
                </button>
              ))}
            </div>
          ) : files.length === 0 ? (
            <div className="px-4 py-8 text-center">
              <p className="text-xs" style={{ color: 'var(--ink-muted)' }}>Vault vazio.</p>
              <p className="text-xs mt-1" style={{ color: 'var(--ink-muted)' }}>Cria um ficheiro ou importa .md</p>
            </div>
          ) : (
            Object.entries(fileTree)
              .sort(([a], [b]) => a.localeCompare(b))
              .map(([dir, dirFiles]) => (
                <div key={dir} className="mb-1">
                  {dir && (
                    <div className="flex items-center gap-1.5 px-4 py-1 mt-1">
                      <Folder size={12} className="shrink-0" style={{ color: 'var(--ink-muted)' }} strokeWidth={1.5} />
                      <span className="text-xs font-medium truncate" style={{ color: 'var(--ink-muted)' }}>{dir}</span>
                    </div>
                  )}
                  {dirFiles
                    .filter(f => f.filename !== '.gitkeep')
                    .map(file => (
                      <div key={file.path} className="flex items-center group">
                        <button
                          onClick={() => openFile(file.path)}
                          className={`flex items-center gap-2 flex-1 min-w-0 py-1.5 text-xs truncate transition-colors ${dir ? 'pl-8 pr-3' : 'px-4'}`}
                          style={{
                            background: selectedFile?.path === file.path ? 'var(--cream-dark)' : '',
                            color: selectedFile?.path === file.path ? 'var(--ink)' : 'var(--ink-light)',
                            fontWeight: selectedFile?.path === file.path ? 500 : 400,
                          }}
                          onMouseEnter={e => { if (selectedFile?.path !== file.path) e.currentTarget.style.background = 'var(--cream)' }}
                          onMouseLeave={e => { if (selectedFile?.path !== file.path) e.currentTarget.style.background = '' }}
                        >
                          <File size={12} className="shrink-0" style={{ color: 'var(--ink-muted)' }} strokeWidth={1.5} />
                          <span className="truncate">{file.filename}</span>
                        </button>
                        <button
                          onClick={e => deleteFile(file.path, e)}
                          className="shrink-0 p-1 mr-1 opacity-0 group-hover:opacity-100 transition-opacity"
                          style={{ color: 'var(--ink-muted)' }}
                        >
                          <Trash2 size={11} />
                        </button>
                      </div>
                    ))}
                </div>
              ))
          )}
        </div>
      </div>

      {/* Editor */}
      <div className="flex-1 flex flex-col bg-white">
        {selectedFile ? (
          <>
            <div className="flex items-center justify-between px-4 py-2.5 border-b" style={{ borderColor: 'var(--border)' }}>
              <span className="text-xs font-mono" style={{ color: 'var(--ink-muted)' }}>{selectedFile.path}</span>
              <div className="flex items-center gap-3">
                {dirty && <span className="text-xs" style={{ color: 'var(--ink-muted)' }}>nao guardado</span>}
                <button
                  onClick={saveFile}
                  disabled={!dirty || saving}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-md transition-colors disabled:opacity-30"
                  style={{ background: 'var(--ink)', color: 'var(--cream)' }}
                  onMouseEnter={e => { if (!e.currentTarget.disabled) e.currentTarget.style.background = 'var(--accent)' }}
                  onMouseLeave={e => (e.currentTarget.style.background = 'var(--ink)')}
                >
                  <Save size={12} strokeWidth={1.5} />
                  {saving ? 'A guardar...' : 'Guardar'}
                </button>
              </div>
            </div>
            <textarea
              value={editContent}
              onChange={e => { setEditContent(e.target.value); setDirty(true) }}
              onKeyDown={e => {
                if ((e.metaKey || e.ctrlKey) && e.key === 's') { e.preventDefault(); saveFile() }
              }}
              className="flex-1 p-5 text-sm font-mono resize-none focus:outline-none leading-relaxed"
              style={{ background: '#fff', color: 'var(--ink)' }}
              spellCheck={false}
            />
          </>
        ) : (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <p className="text-sm" style={{ color: 'var(--ink-muted)' }}>Selecciona um ficheiro para editar</p>
              <p className="text-xs mt-1" style={{ color: 'var(--ink-muted)' }}>ou cria um novo com o botao acima</p>
            </div>
          </div>
        )}
      </div>

      {/* New file modal */}
      {showNewFileModal && (
        <div className="fixed inset-0 flex items-center justify-center z-50" style={{ background: 'rgba(0,0,0,0.3)' }}>
          <div className="bg-white border rounded-lg p-6 w-full max-w-md mx-4 shadow-lg" style={{ borderColor: 'var(--border)' }}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-sm font-semibold" style={{ color: 'var(--ink)' }}>Novo ficheiro</h2>
              <button onClick={() => setShowNewFileModal(false)} style={{ color: 'var(--ink-muted)' }}>
                <X size={16} />
              </button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="text-xs block mb-1" style={{ color: 'var(--ink-muted)' }}>Caminho</label>
                <input
                  type="text"
                  value={newFilePath}
                  onChange={e => setNewFilePath(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && createNewFile()}
                  placeholder="ex: Meta/notas.md"
                  className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none transition-colors"
                  style={{ borderColor: 'var(--border)', color: 'var(--ink)' }}
                  autoFocus
                />
              </div>
              <div>
                <label className="text-xs block mb-1" style={{ color: 'var(--ink-muted)' }}>Conteudo inicial (opcional)</label>
                <textarea
                  value={newFileContent}
                  onChange={e => setNewFileContent(e.target.value)}
                  rows={5}
                  className="w-full border rounded-lg px-3 py-2 text-sm font-mono resize-none focus:outline-none transition-colors"
                  style={{ borderColor: 'var(--border)', color: 'var(--ink)' }}
                />
              </div>
              <div className="flex gap-2 pt-1">
                <button
                  onClick={() => setShowNewFileModal(false)}
                  className="flex-1 px-4 py-2 text-sm border rounded-lg transition-colors"
                  style={{ borderColor: 'var(--border)', color: 'var(--ink-light)' }}
                  onMouseEnter={e => (e.currentTarget.style.background = 'var(--cream-dark)')}
                  onMouseLeave={e => (e.currentTarget.style.background = '')}
                >
                  Cancelar
                </button>
                <button
                  onClick={createNewFile}
                  disabled={!newFilePath.trim() || creating}
                  className="flex-1 px-4 py-2 text-sm rounded-lg transition-colors disabled:opacity-40"
                  style={{ background: 'var(--ink)', color: 'var(--cream)' }}
                  onMouseEnter={e => { if (!e.currentTarget.disabled) e.currentTarget.style.background = 'var(--accent)' }}
                  onMouseLeave={e => (e.currentTarget.style.background = 'var(--ink)')}
                >
                  {creating ? 'A criar...' : 'Criar'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
